// @ts-strict-ignore
import {EventDefinition} from 'common/util/definitions-types';
import {ApiResourceClient} from 'common/util/model-resource/api-resource';
import {apiClient} from 'common/util/api-client';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {getLocation} from 'common/util/url';
import {FetchOptions} from 'common/util/shared-client';
// TODO: consolidate event definition types
import {EventDefinitionFull, NullableId} from 'common/report/queries/app-api/data-definitions-types';

import {appApiUrl, queryApiUrl} from '../utils';
import {purgeFetchCache} from '../utils/fetch-cache';

/**
 *
 * @param enableQueryApi true to use Query API. Query API does not support updates!
 * @returns
 */
function buildEndpoint(enableQueryApi = false) {
  return enableQueryApi
    ? queryApiUrl(`data_definitions/events?project_id=${getProjectId()}&workspace_id=${getWorkspaceId()}`)
    : appApiUrl(`data-definitions/events`, {projectId: getProjectId(), workspaceId: getWorkspaceId()});
}

/**
 * Encapsulates the logic for identifying an event definition, whether it's a custom event or tracked
 * @param event Something that looks like an Event Definition (we have at least 2 different types)
 * @returns
 */
export function eventDefinitionUniqueKey(event: {customEventId?: NullableId; name?: string}): string {
  return event.customEventId ? `$custom_event:${event.customEventId}` : event.name;
}

async function fetchEventDefinitions(fromFetchCache: boolean): Promise<{[key: string]: EventDefinition}> {
  const fetchOptions: FetchOptions = {
    headers: {
      'Request-URL': getLocation().href,
    },
    method: `GET`,
    fromFetchCache,
  };

  fetchOptions.headers[`query_origin`] = `event-definitions-client`;
  // eslint-disable-next-line local-rules/deprecated-member-access
  const result = await apiClient.fetch(buildEndpoint(true), fetchOptions);
  const eventDefinitions: EventDefinition[] = (await result?.json())?.results ?? [];
  // If this is from cache, and it's empty, return null instead of an empty set.
  // Otherwise, ApiResource will signal that the index fetch is done, when it really isn't
  if (!eventDefinitions.length && fromFetchCache) {
    return null;
  }
  return eventDefinitions.reduce((acc, eventDefinition) => {
    acc[eventDefinitionUniqueKey(eventDefinition)] = eventDefinition;
    return acc;
  }, {});
}

export async function update(updatePayload: Partial<EventDefinition>): Promise<EventDefinition> {
  return apiClient.patch<EventDefinition>(buildEndpoint(), {
    ...updatePayload,
  });
}

export async function bulkUpdate(eventDefUpdates: Array<Partial<EventDefinition>>) {
  return apiClient.patch<EventDefinition[]>(buildEndpoint(), {events: eventDefUpdates});
}

export async function remove(eventsToDelete: Array<Partial<EventDefinition>>) {
  return apiClient.delete<void>(buildEndpoint(), {body: JSON.stringify({events: eventsToDelete})});
}

export async function getById(id: number): Promise<[EventDefinitionFull]> {
  return apiClient.get<[EventDefinitionFull]>(buildEndpoint(true) + `&id[]=${id}`);
}

export async function getByName(name: string): Promise<[EventDefinitionFull]> {
  return apiClient.get<[EventDefinitionFull]>(buildEndpoint(true) + `&name[]=${name}`);
}

export const eventDefinitionsClient: ApiResourceClient<EventDefinition> = {
  index() {
    return {
      cache: fetchEventDefinitions(true),
      network: fetchEventDefinitions(false),
    };
  },

  get() {
    throw new Error(`Getting a single event definitions is not supported`);
  },

  purgeCache() {
    purgeFetchCache({everything: true, path: buildEndpoint()});
  },
};
